<template>
  <div id="coupons" class="align-start fill-height">
    <i-overlay
      v-if="loading && $vuetifyDisplay.smAndDown"
      text="Loading coupons..."
    />

    <v-row v-else class="g-container" no-gutters>
      <v-dialog
        v-if="couponDialog"
        v-model="couponDialog"
        max-width="750px"
        persistent
        @click:outside="resetEdition"
        @keydown.esc="resetEdition"
      >
        <i-edit-coupons
          :coupon="editableCoupon"
          @cancel="resetEdition"
          @save="reload"
        ></i-edit-coupons>
      </v-dialog>

      <v-dialog
        v-if="confirmationDialog"
        v-model="confirmationDialog"
        width="400px"
        @click:outside="resetDeletion"
        @keydown.esc="resetDeletion"
      >
        <i-confirmation-dialog
          action-text="Delete"
          :submitting="deletingCoupon || updatingCoupon"
          :text="confirmationDialogText"
          @cancel="resetDeletion"
          @confirm="confirmDeletion"
        />
      </v-dialog>

      <v-col cols="12">
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <template>
          <v-row>
            <v-col cols="12" md="3" lg="2">
              <v-btn
                large
                rounded
                class="i-btn-block-secondary g-full-width"
                @click="editCoupon()"
              >
                Add Coupon
              </v-btn>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="12" md="3" lg="2">
              <i-instructions
                instructionKey="data-maintenance/coupons"
              ></i-instructions>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="4" md="3" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{ 'i-btn-select-active': selectedStatus === 'ACTIVE' }"
                @click="selectedStatus = 'ACTIVE'"
                >Active</v-btn
              >
            </v-col>
            <v-col cols="4" md="3" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{
                  'i-btn-select-active': selectedStatus === 'UPCOMING'
                }"
                @click="selectedStatus = 'UPCOMING'"
                >Upcoming</v-btn
              >
            </v-col>
            <v-col cols="4" md="3" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{ 'i-btn-select-active': selectedStatus === 'PAST' }"
                @click="selectedStatus = 'PAST'"
                >Past</v-btn
              >
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="12" lg="6">
              <i-local-filter
                :filterPlaceholder="'Search Coupons by Target Type or Target(s) or Geography'"
                @filter="localFilter"
              />
            </v-col>
          </v-row>
        </template>

        <template>
          <v-row>
            <v-col cols="12">
              <template v-if="!loading && filteredCouponsByStatus.length === 0">
                <i-empty-results hideButton :title="emptyFilteredResultsText" />
              </template>

              <template v-else>
                <template v-if="$vuetifyDisplay.smAndDown">
                  <v-col
                    v-for="item in filteredCouponsByStatus"
                    :key="item.id"
                    class="mt-0 pa-0"
                  >
                    <v-card class="rounded-2 pa-4 mb-6">
                      <v-row no-gutters>
                        <v-col cols="6" class="py-1">
                          <span
                            ><strong>{{ headers[0].text }}</strong></span
                          >
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <v-chip
                            v-if="item.targetStores"
                            small
                            color="secondary"
                            >{{ isILR ? 'CUSTOMER' : 'RETAILER' }}</v-chip
                          >
                          <v-chip
                            v-else-if="item.targetBannerGroups"
                            small
                            color="accent"
                            >BANNER</v-chip
                          >
                          <v-chip
                            v-else-if="item.targetMultiStoreOperators"
                            small
                            color="primary"
                            >MSO</v-chip
                          >
                          <v-chip v-else small color="light-green accent-2"
                            >ALL</v-chip
                          >
                        </v-col>
                      </v-row>

                      <v-divider class="my-2"></v-divider>

                      <v-row no-gutters>
                        <v-col cols="6" class="py-1">
                          <span>{{ headers[1].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span v-if="item.targetStores">
                            <span
                              v-for="item in item.targetStores"
                              :key="item.storeId"
                            >
                              {{ item.storeName }}
                              <br />
                            </span>
                          </span>
                          <span v-else-if="item.targetBannerGroups">
                            <span
                              v-for="item in item.targetBannerGroups"
                              :key="item.bannerGroupId"
                            >
                              {{ item.bannerGroupName }}
                              <br />
                            </span>
                          </span>
                          <span v-else-if="item.targetMultiStoreOperators">
                            <span
                              v-for="item in item.targetMultiStoreOperators"
                              :key="item.multiStoreOperatorId"
                            >
                              {{ item.multiStoreOperatorName }}
                              <br />
                            </span>
                          </span>
                          <span v-else> - </span>
                        </v-col>

                        <v-col cols="6" class="py-1">
                          <span>{{ headers[2].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span v-if="item.targetStoreDeliveryAddressStates">
                            <span
                              v-for="item in item.targetStoreDeliveryAddressStates"
                              :key="item.state"
                            >
                              {{ item.state }}
                              <br />
                            </span>
                          </span>
                          <span
                            v-else-if="item.targetStoreDeliveryAddressZones"
                          >
                            <span
                              v-for="item in item.targetStoreDeliveryAddressZones"
                              :key="item.zoneCode"
                            >
                              {{ item.zoneCode }}
                              <br />
                            </span>
                          </span>
                          <span v-else-if="item.targetGeography === 'STORE'">{{
                            isILR ? 'Customer' : 'Retailer'
                          }}</span>
                          <span v-else>{{
                            $filters.sentenceCase(item.targetGeography)
                          }}</span>
                        </v-col>

                        <v-col cols="6" class="py-1">
                          <span>{{ headers[3].text }}</span>
                          <v-icon
                            class="i-tooltip-color pb-1"
                            small
                            v-tooltip="
                              'Minimum order spend required to activate Coupon Discount'
                            "
                          >
                            mdi-help-circle-outline
                          </v-icon>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span>
                            {{
                              $filters.currency(
                                item.minimumOrderValue.amountExGst
                              )
                            }}
                          </span>
                        </v-col>

                        <v-col cols="6" class="py-1">
                          <span>{{ headers[4].text }}</span>
                          <v-icon
                            class="i-tooltip-color pb-1"
                            small
                            v-tooltip="
                              'Applies if retailer order achieves Coupon Spend & Save'
                            "
                          >
                            mdi-help-circle-outline
                          </v-icon>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span>
                            {{ $filters.currency(item.amountOff.amountExGst) }}
                          </span>
                        </v-col>

                        <v-col cols="6" class="py-1">
                          <span>
                            {{ headers[5].text }}
                          </span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span>{{ $filters.formatDate(item.startDate) }}</span>
                        </v-col>

                        <v-col cols="6" class="py-1">
                          <span>
                            {{ headers[6].text }}
                          </span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <div
                            class="py-1"
                            style="font-style: inherit; display: inline-block"
                            :class="{
                              'i-status-block-red': item.endsToday,
                              'g-border-radius-s': item.endsToday
                            }"
                          >
                            <span>{{ $filters.formatDate(item.endDate) }}</span>
                          </div>
                        </v-col>
                      </v-row>

                      <template v-if="!(item.status === 'PAST')">
                        <v-divider class="my-2"></v-divider>

                        <v-row no-gutters>
                          <v-col cols="12" class="py-1">
                            <v-btn
                              class="i-btn-actions-blue g-full-width"
                              @click="editCoupon(item)"
                            >
                              <span>Edit Coupon</span>
                              <v-icon small class="ml-1"
                                >mdi-circle-edit-outline</v-icon
                              >
                            </v-btn>
                          </v-col>
                          <v-col cols="12" class="py-1">
                            <v-btn
                              class="i-btn-actions-red g-full-width"
                              :disabled="
                                item.endsToday || item.status === 'PAST'
                              "
                              @click="startDeletion(item.id)"
                            >
                              <span>Delete Coupon</span>
                              <v-icon small class="ml-1"
                                >mdi-delete-outline</v-icon
                              >
                            </v-btn>
                          </v-col>
                        </v-row>
                      </template>
                    </v-card>
                  </v-col>
                </template>

                <template v-else>
                  <v-data-table
                    class="pt-2 elevation-1"
                    :headers="headers"
                    :hide-default-footer="selectedStatus === 'ACTIVE'"
                    :items="filteredCouponsByStatus"
                    :items-per-page="-1"
                    :loading="loading"
                    :no-data-text="emptyFilteredResultsText"
                    :no-results-text="emptyFilteredResultsText"
                  >
                    <template #[`item.targetType`]="{ item }">
                      <v-chip
                        v-if="item.targetStores"
                        small
                        color="secondary"
                        >{{ isILR ? 'CUSTOMER' : 'RETAILER' }}</v-chip
                      >
                      <v-chip
                        v-else-if="item.targetBannerGroups"
                        small
                        color="accent"
                        >BANNER</v-chip
                      >
                      <v-chip
                        v-else-if="item.targetMultiStoreOperators"
                        small
                        color="primary"
                        >MSO</v-chip
                      >
                      <v-chip v-else small color="light-green accent-2"
                        >ALL</v-chip
                      >
                    </template>

                    <template #[`item.targetValue`]="{ item }">
                      <span v-if="item.targetStores">
                        <span
                          v-for="item in item.targetStores"
                          :key="item.storeId"
                        >
                          {{ item.storeName }}
                          <br />
                        </span>
                      </span>
                      <span v-else-if="item.targetBannerGroups">
                        <span
                          v-for="item in item.targetBannerGroups"
                          :key="item.bannerGroupId"
                        >
                          {{ item.bannerGroupName }}
                          <br />
                        </span>
                      </span>
                      <span v-else-if="item.targetMultiStoreOperators">
                        <span
                          v-for="item in item.targetMultiStoreOperators"
                          :key="item.multiStoreOperatorId"
                        >
                          {{ item.multiStoreOperatorName }}
                          <br />
                        </span>
                      </span>
                      <span v-else> - </span>
                    </template>

                    <template #[`item.targetGeography`]="{ item }">
                      <span v-if="item.targetStoreDeliveryAddressStates">
                        <span
                          v-for="item in item.targetStoreDeliveryAddressStates"
                          :key="item.state"
                        >
                          {{ item.state }}
                          <br />
                        </span>
                      </span>
                      <span v-else-if="item.targetStoreDeliveryAddressZones">
                        <span
                          v-for="item in item.targetStoreDeliveryAddressZones"
                          :key="item.zoneCode"
                        >
                          {{ item.zoneCode }}
                          <br />
                        </span>
                      </span>
                      <span v-else-if="item.targetGeography === 'STORE'">{{
                        isILR ? 'Customer' : 'Retailer'
                      }}</span>
                      <span v-else>{{
                        $filters.sentenceCase(item.targetGeography)
                      }}</span>
                    </template>

                    <template
                      #[`header.minimumOrderValue.amountExGst`]="{ header }"
                    >
                      {{ header.text }}
                      <v-icon
                        class="i-tooltip-color pb-1"
                        small
                        v-tooltip="
                          'Minimum order spend required to activate Coupon Discount'
                        "
                      >
                        mdi-help-circle-outline
                      </v-icon>
                    </template>

                    <template #[`header.amountOff.amountExGst`]="{ header }">
                      {{ header.text }}
                      <v-icon
                        class="i-tooltip-color pb-1"
                        small
                        v-tooltip="
                          'Applies if retailer order achieves Coupon Spend & Save'
                        "
                      >
                        mdi-help-circle-outline
                      </v-icon>
                    </template>

                    <template #[`item.startDate`]="{ item }">
                      {{ $filters.formatDate(item.startDate) }}
                    </template>

                    <template #[`item.endDate`]="{ item }">
                      <div
                        class="pa-1"
                        :class="{
                          'i-status-block-red': item.endsToday,
                          'g-border-radius-s': item.endsToday
                        }"
                        style="font-size: inherit"
                      >
                        {{ $filters.formatDate(item.endDate) }}
                      </div>
                    </template>

                    <template
                      #[`item.minimumOrderValue.amountExGst`]="{ item }"
                    >
                      {{
                        $filters.currency(item.minimumOrderValue.amountExGst)
                      }}
                    </template>

                    <template #[`item.amountOff.amountExGst`]="{ item }">
                      {{ $filters.currency(item.amountOff.amountExGst) }}
                    </template>

                    <template #[`item.actions`]="{ item }">
                      <v-btn
                        color="secondary"
                        :disabled="item.status === 'PAST'"
                        icon
                        v-tooltip="{
                          content: 'Edit',
                          visible: $vuetifyDisplay.mdAndDown ? false : undefined
                        }"
                        @click="editCoupon(item)"
                      >
                        <v-icon>mdi-circle-edit-outline</v-icon>
                      </v-btn>

                      <v-btn
                        color="error"
                        :disabled="item.endsToday || item.status === 'PAST'"
                        icon
                        v-tooltip="{
                          content: 'Delete',
                          visible: $vuetifyDisplay.mdAndDown ? false : undefined
                        }"
                        @click="startDeletion(item)"
                      >
                        <v-icon>mdi-delete-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </template>
              </template>
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment-timezone'
import { mapActions, mapState, mapGetters } from 'vuex'
import { fetchCoupons, deleteCoupon, updateCoupon } from '@/services/coupons'
import FiltersHelper from '@/modules/dataMaintenance/helper/FiltersHelper'
import iEditCoupons from '../components/EditCoupons'
import iConfirmationDialog from '@/components/ConfirmationDialog'
import iLocalFilter from '@/components/LocalFilter'
import iOverlay from '@/components/Overlay'
import iInstructions from '@/components/Instructions'
import iEmptyResults from '@/components/EmptyResults'

export default {
  name: 'coupons',

  components: {
    iEmptyResults,
    iInstructions,
    iEditCoupons,
    iConfirmationDialog,
    iLocalFilter,
    iOverlay
  },

  data: () => ({
    couponDialog: false,
    confirmationDialog: false,
    confirmationDialogText: '',
    coupons: [],
    currentDate: moment().format('YYYY-MM-DD'),
    deletingCoupon: false,
    editableCoupon: {},
    filterText: '',
    headers: [
      {
        text: 'TARGET TYPE',
        value: 'targetType',
        width: 150,
        align: 'center',
        sortable: false
      },
      {
        text: 'TARGET(S)',
        value: 'targetValue',
        width: '20%',
        align: 'center',
        sortable: false
      },
      {
        text: 'GEOGRAPHY',
        width: 150,
        value: 'targetGeography',
        align: 'center'
      },
      {
        text: 'COUPON SPEND & SAVE',
        value: 'minimumOrderValue.amountExGst',
        align: 'center'
      },
      {
        text: 'COUPON DISCOUNT',
        value: 'amountOff.amountExGst',
        align: 'center'
      },
      { text: 'START DATE', value: 'startDate', align: 'center' },
      { text: 'END DATE', value: 'endDate', align: 'center' },
      { text: 'ACTIONS', value: 'actions', sortable: false, align: 'center' }
    ],
    loading: false,
    menuItems: [
      { title: 'Active Coupons', value: 'ACTIVE' },
      { title: 'Upcoming Coupons', value: 'UPCOMING' },
      { title: 'Past Coupons', value: 'PAST' }
    ],
    selectedCoupon: null,
    selectedStatus: 'ACTIVE',
    updatingCoupon: false
  }),

  computed: {
    ...mapGetters('tenant', ['featureCouponsEnabled']),
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapGetters('tenant', ['isILR']),

    emptyFilteredResultsText() {
      return `No ${this.selectedStatus} Coupons found`
    },

    mappedCoupons() {
      return this.coupons.map((coupon) => {
        return {
          ...coupon,
          endsToday: coupon.endDate === this.currentDate
        }
      })
    },

    couponsByStatus() {
      return this.mappedCoupons
        .filter((c) => c.status === this.selectedStatus)
        .sort((c1, c2) => {
          if (c1.status === 'ACTIVE')
            return c1.startDate > c2.startDate ? -1 : 1
          if (c1.status === 'UPCOMING')
            return c1.startDate < c2.startDate ? -1 : 1
          if (c1.status === 'PAST') return c1.endDate > c2.endDate ? -1 : 1
        })
    },

    filteredCouponsByStatus() {
      const searchTextLowerCase = this.filterText.toLowerCase()

      return this.couponsByStatus.filter((coupon) => {
        return (
          coupon.targetGeography.toLowerCase().includes(searchTextLowerCase) ||
          coupon.targetStoreDeliveryAddressStates?.filter((s) =>
            s.state.toLowerCase().includes(searchTextLowerCase)
          ).length > 0 ||
          coupon.targetStoreDeliveryAddressZones?.filter((z) =>
            z.zoneCode.toLowerCase().includes(searchTextLowerCase)
          ).length > 0 ||
          FiltersHelper.filterByTargetType(coupon, searchTextLowerCase) ||
          FiltersHelper.filterByTargets(coupon, searchTextLowerCase)
        )
      })
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    localFilter(filterText) {
      if (typeof filterText !== 'string') filterText = ''
      this.filterText = filterText
    },

    // ***
    // Functions related to handling actions
    // ***

    startDeletion(item) {
      this.selectedCoupon = item
      this.confirmationDialogText =
        this.selectedCoupon.status === 'ACTIVE'
          ? 'Active Coupon will end midnight today (End Date auto updates)'
          : 'Confirm deletion of this Coupon'
      this.confirmationDialog = true
    },

    confirmDeletion() {
      this.selectedCoupon.status === 'ACTIVE'
        ? this.endCoupon(this.selectedCoupon)
        : this.deleteCoupon(this.selectedCoupon.id)
    },

    reload() {
      this.getCoupons()
      this.resetEdition()
    },

    resetDeletion() {
      this.confirmationDialog = false
      this.confirmationDialogText = ''
      this.selectedCoupon = null
    },

    resetEdition() {
      this.editableCoupon = {}
      this.couponDialog = false
    },

    // ***
    // Functions related to coupons data
    // ***

    editCoupon(coupon = {}) {
      this.editableCoupon = _.cloneDeep(coupon)
      this.couponDialog = true
    },

    deleteCoupon(couponId) {
      this.deletingCoupon = true
      this.addNotification({
        color: 'secondary',
        text: 'Deleting coupon...'
      })

      deleteCoupon(this.selectedSupplierId, couponId)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Coupon has been deleted!'
          })
          this.getCoupons()
        })
        .catch((err) => {
          console.log(err)
          this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.deletingCoupon = false
          this.resetDeletion()
        })
    },

    endCoupon(coupon) {
      this.updatingCoupon = true
      this.addNotification({
        color: 'secondary',
        text: 'Ending Coupon...'
      })

      const payload = {
        endDate: this.currentDate,
        startDate: coupon.startDate,
        amountOffExGst: coupon.amountOff.amountExGst,
        minimumOrderValue: coupon.minimumOrderValue.amountExGst,
        targetBannerGroupIds:
          coupon.targetBannerGroups?.map(
            (bannerGroup) => bannerGroup.bannerGroupId
          ) || [],
        targetMultiStoreOperatorIds:
          coupon.targetMultiStoreOperators?.map(
            (mso) => mso.multiStoreOperatorId
          ) || [],
        targetStoreDeliveryAddressStates:
          coupon.targetStoreDeliveryAddressStates?.map(
            (state) => state.state
          ) || [],
        targetStoreDeliveryAddressZones:
          coupon.targetStoreDeliveryAddressZones?.map(
            (zone) => zone.zoneCode
          ) || []
      }

      updateCoupon(this.selectedSupplierId, this.selectedCoupon.id, payload)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Coupon end date has been updated to today!'
          })
        })
        .catch((err) => {
          console.log(err)
          this.addNotification({
            color: 'error',
            text: 'Coupon could not be updated, please try again...'
          })
        })
        .finally(() => {
          this.updatingCoupon = false
          this.resetDeletion()
          this.reload()
        })
    },

    getCoupons() {
      this.loading = true
      fetchCoupons(this.selectedSupplierId)
        .then((response) => {
          this.coupons = response
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Coupons could not be loaded, please try again...'
          })
        })
        .finally(() => {
          this.loading = false
        })
    }
  },

  mounted() {
    if (!this.featureCouponsEnabled) {
      this.$router.replace({ name: 'dashboard' })
      return
    }
    this.getCoupons()
  }
}
</script>

<style lang="scss" scoped>
// #coupons {}
</style>
